import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import MobilePageLayout from '../../../components/layout/MobilePageLayout';
import { convertToVwMob } from '../../../Utils/responsive';
import { useDonorManagement } from '../hooks/useDonorManagement';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectSelectedProject } from '../../../Module/Project/Presentation/projectSelectors';
import { getProject } from '../../../Module/Project/Presentation/projectActions';
import ProjectDetailHeader from '../../../components/common/ProjectDetailHeader';
import DSLoading from '../../../components/designSystem/DSLoading';
import DSBottomSheet from '../../../components/designSystem/DSBottomSheet';
import kakaoIcon from '../../../assets/Icon/project/kakao_icon.png';
import emailIcon from '../../../assets/Icon/project/email_icon.png';
import kakaoExampleImg from '../../../assets/img/share_modal/kakao_example.png';
import emailExampleImg from '../../../assets/img/share_modal/email_example.png';
import { useTranslation } from 'react-i18next';
import { openMailClient } from '../../../Utils/mail';

const DonorManagementMob = () => {
  const { t } = useTranslation('translation');
  const location = useLocation();
  const dispatch = useAppDispatch();
  const selectedProject = useAppSelector(selectSelectedProject);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [sharePreviewType, setSharePreviewType] = useState<'kakao' | 'email' | null>(null);
  const projectId = (location.state as { projectId?: string } | undefined)?.projectId;

  useEffect(() => {
    if (projectId) {
      // 선택된 프로젝트가 없거나, 현재 선택된 프로젝트와 파라미터의 projectId가 다른 경우 정보 요청
      if (!selectedProject || selectedProject.project_id !== projectId) {
        dispatch(getProject({ project_id: projectId }));
      }
    }
  }, [dispatch, projectId, selectedProject]);

  const currentProject = useAppSelector(selectSelectedProject);
  const projectTitle = currentProject?.title || t('common.projectTitlePlaceholder');

  const effectiveProjectId = projectId ?? currentProject?.project_id;
  // API 연동 훅 사용
  const { summary, donors, hasData, isLoading } = useDonorManagement(effectiveProjectId);

  const runKakaoShare = () => {
    const KAKAO_KEY = 'd7fe97bd56b8e854e11feab6313e4b0a';
    const shareTitle = projectTitle;
    const projectUrl = `http://go.MissionFund.org/${effectiveProjectId || ''}`;

    if (!(window as any).Kakao) {
      const script = document.createElement('script');
      script.src = 'https://t1.kakaocdn.net/kakao_js_sdk/2.6.0/kakao.min.js';
      script.integrity = 'sha384-6MFdIr0zOira1CHQkedUqJVqe0ryQtcLs7ufRWw+T8rCXNKryCOgM/c7x5+z2JK7';
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        if (!(window as any).Kakao.isInitialized()) {
          (window as any).Kakao.init(KAKAO_KEY);
        }
        shareToKakao(shareTitle, projectUrl);
      };
      document.head.appendChild(script);
    } else {
      if (!(window as any).Kakao.isInitialized()) {
        (window as any).Kakao.init(KAKAO_KEY);
      }
      shareToKakao(shareTitle, projectUrl);
    }
  };

  const shareToKakao = (title: string, url: string) => {
    (window as any).Kakao.Share.sendDefault({
      objectType: 'feed',
      content: {
        title: title,
        description: `${t('common.shareModal.descLine1')} ${t('common.shareModal.descLine2')}`,
        imageUrl: 'https://missionfund.org/static/media/main_logo.png',
        link: {
          mobileWebUrl: url,
          webUrl: url,
        },
      },
      buttons: [
        {
          title: t('common.shareModal.buttonDetail'),
          link: {
            mobileWebUrl: url,
            webUrl: url,
          },
        },
      ],
    });
  };

  const runEmailShare = () => {
    const shareTitle = projectTitle;
    const projectUrl = `http://go.MissionFund.org/${effectiveProjectId || ''}`;
    const subject = t('common.shareModal.emailSubject', { title: shareTitle });
    const body = t('common.shareModal.emailBody', { title: shareTitle, url: projectUrl });
    openMailClient({ subject, body });
  };

  const handleBulkEmail = () => {
    const shareTitle = projectTitle;
    const projectUrl = `http://go.MissionFund.org/${effectiveProjectId || ''}`;
    const subject = t('common.shareModal.emailSubject', { title: shareTitle });
    const body = t('common.shareModal.emailBody', { title: shareTitle, url: projectUrl });
    openMailClient({ subject, body });
  };

  const handleDonorEmail = (email?: string) => {
    const to = email?.trim();
    if (!to) return;
    const shareTitle = projectTitle;
    const projectUrl = `http://go.MissionFund.org/${effectiveProjectId || ''}`;
    const subject = t('common.shareModal.emailSubject', { title: shareTitle });
    const body = t('common.shareModal.emailBody', { title: shareTitle, url: projectUrl });
    openMailClient({ to, subject, body });
  };

  const handleKakaoShare = () => {
    setIsShareModalOpen(false);
    setSharePreviewType('kakao');
  };

  const handleEmailShare = () => {
    setIsShareModalOpen(false);
    setSharePreviewType('email');
  };

  const handleCloseSharePreview = () => {
    setSharePreviewType(null);
  };

  const handleBackSharePreview = () => {
    setSharePreviewType(null);
    setIsShareModalOpen(true);
  };

  const handleConfirmSharePreview = () => {
    if (sharePreviewType === 'kakao') {
      runKakaoShare();
    } else if (sharePreviewType === 'email') {
      runEmailShare();
    }
    setSharePreviewType(null);
  };

  const handleCopyLink = async () => {
    const url = `http://go.MissionFund.org/${effectiveProjectId || ''}`;
    try {
      await navigator.clipboard.writeText(url);
      alert(t('common.messages.linkCopied'));
      setIsShareModalOpen(false);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      alert(t('common.messages.linkCopied'));
      setIsShareModalOpen(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR').format(amount) + t('common.units.won');
  };

  const handleExcelDownload = () => {
    if (!donors || donors.length === 0) return;

    const headerStyle = {
      fill: { fgColor: { rgb: 'D3D3D3' } },
      font: { bold: false, color: { rgb: '000000' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const cellStyle = {
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const rightAlignStyle = {
      alignment: { horizontal: 'right', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const commentStyle = {
      alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const headers = [
      'No', 'Donor name', 'Phone', 'Email', 'Address',
      'Donation Type', 'Payment', 'Status', 'History',
      'Amount', 'Date', 'Comments',
    ];

    const data = donors.map((d, idx) => ([
      donors.length - idx,
      d.name,
      d.phone,
      d.email,
      d.address || '',
      d.donationType,
      d.method.toUpperCase(),
      d.status === 'cancel' ? 'cancelled' : (d.status === 'fail' ? 'failed' : 'active'),
      '',
      `₩${new Intl.NumberFormat('ko-KR').format(d.amount)}`,
      d.date,
      d.message,
    ]));

    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    ws['!cols'] = [
      { wch: 5 }, { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 20 }, { wch: 15 },
      { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 80 },
    ];

    if (ws['!ref']) {
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let c = range.s.c; c <= range.e.c; c += 1) {
        const address = XLSX.utils.encode_cell({ r: 0, c });
        if (ws[address]) ws[address].s = headerStyle;
      }
      for (let r = 1; r <= range.e.r; r += 1) {
        for (let c = range.s.c; c <= range.e.c; c += 1) {
          const address = XLSX.utils.encode_cell({ r, c });
          if (ws[address]) {
            ws[address].s = cellStyle;
            if (c === 9) ws[address].s = rightAlignStyle;
            if (c === 11) ws[address].s = commentStyle;
          }
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Donations');

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const projectIdForFile = String(currentProject?.project_id ?? effectiveProjectId ?? 'project')
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, '_');
    const fileName = `onlove_donations_${projectIdForFile}_${yyyy}${mm}${dd}.xlsx`;

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), fileName);
  };

  const renderEmptyState = () => (
    <EmptyStateWrapper>
      <EmptyTitleGroup>
        <EmptyTitleBlue>
          {t('common.empty.titleBlueLine1')} <br /> {t('common.empty.titleBlueLine2')}
        </EmptyTitleBlue>
        <EmptyTitleBlack>
          {t('common.empty.titleBlackLine1')} <br /> {t('common.empty.titleBlackLine2')}
        </EmptyTitleBlack>
      </EmptyTitleGroup>
      <ShareBtn onClick={() => setIsShareModalOpen(true)}>
        <ShareIcon src="/Image/posts/shareIcon.png" alt={t('common.empty.shareAlt')} />
        {t('common.empty.share')}
      </ShareBtn>
    </EmptyStateWrapper>
  );

  return (
    <MobilePageLayout title={t('page.donorManagement.pageTitle')}>
      <Container>
        <ProjectDetailHeader title={projectTitle} projectId={currentProject?.project_id} variant="mobile" />
        <PageTitle>{t('page.donorManagement.pageTitle')}</PageTitle>

        {isLoading ? (
          <DSLoading />
        ) : !hasData ? (
          renderEmptyState()
        ) : (
          <>
            <SummaryGrid>
              <SummaryCard variant="yellow">
                <CardLabelGroup>
                  <CardTitle>{t('page.donorManagement.summary.totalTitle')}</CardTitle>
                  <CardDesc>{t('page.donorManagement.summary.totalDesc')}</CardDesc>
                </CardLabelGroup>
                <CardValue>{summary.total}{t('common.units.personSuffix')}</CardValue>
              </SummaryCard>

              <SummaryCard variant="white">
                <CardLabelGroup>
                  <CardTitle>{t('page.donorManagement.summary.cancelledTitle')}</CardTitle>
                  <CardDesc>{t('page.donorManagement.summary.cancelledDesc')}</CardDesc>
                </CardLabelGroup>
                <CardValue>{summary.cancelled}{t('common.units.personSuffix')}</CardValue>
              </SummaryCard>

              <SummaryCard variant="white">
                <CardLabelGroup>
                  <CardTitle>{t('page.donorManagement.summary.failedTitle')}</CardTitle>
                  <CardDesc>{t('page.donorManagement.summary.failedDesc')}</CardDesc>
                </CardLabelGroup>
                <CardValue>{summary.failed}{t('common.units.personSuffix')}</CardValue>
              </SummaryCard>

              <SummaryCard variant="blue">
                <CardLabelGroup>
                  <CardTitle highlight={true}>{t('page.donorManagement.summary.inProgressTitle')}</CardTitle>
                  <CardDesc>{t('page.donorManagement.summary.inProgressDesc')}</CardDesc>
                </CardLabelGroup>
                <CardValue color="blue">{summary.inProgress}{t('common.units.personSuffix')}</CardValue>
              </SummaryCard>
            </SummaryGrid>

            <ActionSection>
              <ExcelButtonFull onClick={handleExcelDownload}>
                <DownloadIcon />
                {t('common.actions.downloadExcel')}
              </ExcelButtonFull>
              <EmailAllButtonFull onClick={handleBulkEmail}>
                <EmailIcon />
                {t('page.donorManagement.actions.emailBulk')}
              </EmailAllButtonFull>
              <HelperText>{t('page.donorManagement.helper')}</HelperText>
            </ActionSection>

            <DonorList>
              {donors.map((donor, index) => (
                <DonorItem
                  key={donor.id}
                  isFirst={index === 0}
                  isLast={index === donors.length - 1}
                  isCancelled={donor.status === 'cancel'}
                >
                  <ProfileSection>
                    <ProfileImg src={donor.profileImg} alt={donor.name} />
                    <ProfileInfo>
                      <DonorName color={donor.status === 'cancel' ? 'gray' : 'black'}>
                        {donor.name}
                      </DonorName>
                      <BadgeGroup>
                        <Badge type={donor.method}>{t(`common.payment.method.${donor.method}`)}</Badge>
                        <Badge type={donor.status}>{t(`common.payment.status.${donor.status}`)}</Badge>
                      </BadgeGroup>
                    </ProfileInfo>
                  </ProfileSection>

                  <DonorContactInfo>
                    {donor.email?.trim() && (
                      <ContactItem>
                        <ContactEmailIcon />
                        {donor.email}
                      </ContactItem>
                    )}
                    <ContactItem>
                      <ContactPhoneIcon />
                      {donor.phone}
                    </ContactItem>
                    <ContactItem>
                      <ContactDateIcon />
                      {donor.date} {t('common.labels.registered')}
                    </ContactItem>
                  </DonorContactInfo>

                  {donor.message ? <DonorMessage>{donor.message}</DonorMessage> : null}

                  <AmountSectionMob>
                    <AmountBoxMob>
                      <AmountLabel>{t('page.donorManagement.labels.amount')}</AmountLabel>
                      <AmountValue>{formatCurrency(donor.amount)}</AmountValue>
                    </AmountBoxMob>
                    <AmountBoxMob>
                      <AmountLabel>{t('page.donorManagement.labels.count')}</AmountLabel>
                      <AmountValue>{donor.count}{t('common.units.timesSuffix')}</AmountValue>
                    </AmountBoxMob>
                  </AmountSectionMob>

                  {donor.email?.trim() && (
                  <EmailButtonMob type="button" onClick={() => handleDonorEmail(donor.email)}>
                    <EmailIconSmallBlue />
                    {t('page.donorManagement.actions.sendEmail')}
                  </EmailButtonMob>
                  )}
                </DonorItem>
              ))}
            </DonorList>
          </>
        )}
      </Container>

      <DSBottomSheet open={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} padding="0">
        <ShareBottomSheetWrapper>
          <ShareHeader>
            <TitleRow>
              <ShareTitle>{t('page.donorManagement.shareModal.title')}</ShareTitle>
              <CloseButton onClick={() => setIsShareModalOpen(false)} aria-label="close">
                ✕
              </CloseButton>
            </TitleRow>
            <ShareDescription>
              {t('common.shareModal.descLine1')}
              <br />
              {t('common.shareModal.descLine2')}
            </ShareDescription>
          </ShareHeader>

          <ShareOptionsContainer>
            <ShareOption onClick={handleKakaoShare}>
              <KakaoIconBox>
                <KakaoIconImg src={kakaoIcon} alt={t('common.shareModal.kakaoAlt')} />
              </KakaoIconBox>
              <ShareOptionText>{t('common.shareModal.kakaoOption')}</ShareOptionText>
            </ShareOption>

            <ShareOption onClick={handleEmailShare}>
              <EmailIconBox>
                <EmailIconLarge src={emailIcon} alt={t('common.shareModal.emailAlt')} />
              </EmailIconBox>
              <ShareOptionText>{t('common.shareModal.emailOption')}</ShareOptionText>
            </ShareOption>
          </ShareOptionsContainer>

          <CopyLinkButtonContainer>
            <CopyLinkButton onClick={handleCopyLink}>
              <CopyIcon />
              {t('common.shareModal.copyLink')}
            </CopyLinkButton>
          </CopyLinkButtonContainer>
        </ShareBottomSheetWrapper>
      </DSBottomSheet>

      <DSBottomSheet open={Boolean(sharePreviewType)} onClose={handleCloseSharePreview} padding="0">
        {sharePreviewType && (
          <SharePreviewSheet>
            <SharePreviewHeader>
              <SharePreviewTitleRow>
                <SharePreviewTitle>
                  {sharePreviewType === 'kakao' ? t('common.shareModal.kakaoOption') : t('common.shareModal.emailOption')}
                </SharePreviewTitle>
                <SharePreviewCloseButton onClick={handleCloseSharePreview} aria-label="close">
                  ✕
                </SharePreviewCloseButton>
              </SharePreviewTitleRow>
              <SharePreviewDesc>
                {sharePreviewType === 'kakao' ? '카카오톡으로 새 소식을 전합니다.' : '이메일로 새 소식을 전합니다.'}
              </SharePreviewDesc>
            </SharePreviewHeader>

            <SharePreviewBody>
              <SharePreviewImage
                src={sharePreviewType === 'kakao' ? kakaoExampleImg : emailExampleImg}
                alt={sharePreviewType === 'kakao' ? t('common.shareModal.kakaoAlt') : t('common.shareModal.emailAlt')}
              />
            </SharePreviewBody>

            <SharePreviewFooter>
              <SharePreviewBackButton onClick={handleBackSharePreview}>
                {t('common.actions.back')}
              </SharePreviewBackButton>
              <SharePreviewSendButton onClick={handleConfirmSharePreview}>
                보내기
              </SharePreviewSendButton>
            </SharePreviewFooter>
          </SharePreviewSheet>
        )}
      </DSBottomSheet>
    </MobilePageLayout>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: ${convertToVwMob(24)};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin-bottom: ${convertToVwMob(20)};
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${convertToVwMob(8)};
  margin-bottom: ${convertToVwMob(20)};
`;

const SummaryCard = styled.div<{ variant: 'yellow' | 'white' | 'blue' }>`
  height: ${convertToVwMob(163)};
  padding: ${convertToVwMob(16)} ${convertToVwMob(20)};
  border-radius: ${convertToVwMob(10)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  
  background: ${({ theme, variant }) => {
    if (variant === 'yellow') return theme.colors.yellow[5] || '#fffaea';
    if (variant === 'blue') return theme.colors.primaryBlue[5] || '#f8fdff';
    return 'white';
  }};

  ${({ theme, variant }) => variant === 'yellow' && `border: 1.5px solid ${theme.colors.yellow[50] || '#ffe390'};`}
  ${({ theme, variant }) => variant === 'blue' && `border: 1.5px solid ${theme.colors.primaryBlue[40] || '#2bc2e1'};`}
`;

const CardLabelGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
`;

const CardTitle = styled.span<{ highlight?: boolean }>`
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
  color: ${({ theme, highlight }) => highlight ? theme.colors.primaryBlue[40] : theme.colors.grayScale[90]};
`;

const CardDesc = styled.span`
  font-size: ${convertToVwMob(16)};
  color: ${({ theme }) => theme.colors.grayScale[70]};
`;

const CardValue = styled.span<{ color?: 'blue' }>`
  font-size: ${convertToVwMob(28)};
  font-weight: 500;
  color: ${({ theme, color }) => color === 'blue' ? theme.colors.primaryBlue[40] : theme.colors.grayScale[90]};
`;

const ActionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(12)};
  margin-bottom: ${convertToVwMob(20)};
`;

const ExcelButtonFull = styled.button`
  width: 100%;
  height: ${convertToVwMob(58)};
  background: ${({ theme }) => theme.colors.green[65] || '#21a366'};
  border: none;
  border-radius: ${convertToVwMob(100)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
  color: white;
  cursor: pointer;
`;

const EmailAllButtonFull = styled.button`
  width: 100%;
  height: ${convertToVwMob(58)};
  background: ${({ theme }) => theme.colors.primaryBlue[5] || '#f8fdff'};
  border: 2px solid ${({ theme }) => theme.colors.primaryBlue[40] || '#2bc2e1'};
  border-radius: ${convertToVwMob(100)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primaryBlue[40] || '#2bc2e1'};
  cursor: pointer;
  transition: background 0.15s;

  &:active {
    background: ${({ theme }) => theme.colors.primaryBlue[15] || '#def9ff'};
  }
`;

const HelperText = styled.p`
  font-size: ${convertToVwMob(16)};
  color: ${({ theme }) => theme.colors.grayScale[70]};
  margin: 0;
`;

const DonorList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: ${convertToVwMob(12)};
  overflow: hidden;
`;

const DonorItem = styled.div<{ isFirst: boolean; isLast: boolean; isCancelled?: boolean }>`
  padding: ${convertToVwMob(20)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(16)};
  background: ${({ isCancelled, theme }) => isCancelled ? (theme.colors.grayScale[10] || '#f0f1f2') : 'white'};
  border-bottom: ${({ isLast, theme }) => isLast ? 'none' : `1px solid ${theme.colors.grayScale[20]}`};
`;

const ProfileSection = styled.div`
  display: flex;
  gap: ${convertToVwMob(16)};
  align-items: center;
`;

const ProfileImg = styled.img`
  width: ${convertToVwMob(84)};
  height: ${convertToVwMob(84)};
  border-radius: 50%;
  object-fit: cover;
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
`;

const DonorName = styled.span<{ color: 'black' | 'gray' }>`
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
  color: ${({ theme, color }) => color === 'gray' ? theme.colors.grayScale[70] : theme.colors.grayScale[90]};
`;

const BadgeGroup = styled.div`
  display: flex;
  gap: ${convertToVwMob(4)};
`;

const Badge = styled.span<{ type: 'cms' | 'card' | 'complete' | 'fail' | 'cancel' }>`
  padding: ${convertToVwMob(4)} ${convertToVwMob(10)};
  border-radius: ${convertToVwMob(4)};
  font-size: ${convertToVwMob(12)};
  ${({ theme, type }) => {
    switch (type) {
      case 'cms': return `background: ${theme.colors.primaryBlue[80] || '#026478'}; color: white;`;
      case 'card': return `background: ${theme.colors.primaryBlue[10] || '#ebfcff'}; color: ${theme.colors.primaryBlue[50] || '#25b9d8'};`;
      case 'complete': return `background: ${theme.colors.primaryBlue[20] || '#b7f2ff'}; color: ${theme.colors.grayScale[90]};`;
      case 'fail': return `background: white; border: 1px solid ${theme.colors.other?.red5 || '#ffc0c0'}; color: ${theme.colors.other?.red10 || '#ff0000'};`;
      case 'cancel': return `background: ${theme.colors.grayScale[30] || '#e5e5e5'}; color: ${theme.colors.grayScale[50] || '#bcbcbc'};`;
    }
  }}
`;

const DonorContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(8)};
`;

const ContactItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${convertToVwMob(8)};
  font-size: ${convertToVwMob(16)};
  color: ${({ theme }) => theme.colors.grayScale[70]};
`;

const ContactEmailIcon = styled.div`
  width: ${convertToVwMob(16)};
  height: ${convertToVwMob(16)};
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.3333 2.66666H2.66666C1.93333 2.66666 1.33333 3.26666 1.33333 3.99999V12C1.33333 12.7333 1.93333 13.3333 2.66666 13.3333H13.3333C14.0667 13.3333 14.6667 12.7333 14.6667 12V3.99999C14.6667 3.26666 14.0667 2.66666 13.3333 2.66666ZM13.3333 5.33332L8 8.66666L2.66666 5.33332V3.99999L8 7.33332L13.3333 3.99999V5.33332Z' fill='%23676767'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  flex-shrink: 0;
`;

const ContactPhoneIcon = styled.div`
  width: ${convertToVwMob(16)};
  height: ${convertToVwMob(16)};
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.68 14.91 16.08 14.82 16.43 14.94C17.55 15.31 18.76 15.51 20 15.51C20.55 15.51 21 15.96 21 16.51V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.24 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z' fill='%23676767'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  flex-shrink: 0;
`;

const ContactDateIcon = styled.div`
  width: ${convertToVwMob(16)};
  height: ${convertToVwMob(16)};
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M19 3H18V1H16V3H8V1H6V3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.11 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM19 19H5V8H19V19Z' fill='%23676767'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  flex-shrink: 0;
`;

const DonorMessage = styled.p`
  font-size: ${convertToVwMob(16)};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  line-height: 1.5;
  margin: 0;
  min-height: ${convertToVwMob(48)};
  display: flex;
  align-items: center;
`;

const AmountSectionMob = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(8)};
`;

const AmountBoxMob = styled.div`
  padding: ${convertToVwMob(10)} ${convertToVwMob(20)};
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: ${convertToVwMob(10)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
`;

const AmountLabel = styled.span`
  font-size: ${convertToVwMob(16)};
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const AmountValue = styled.span`
  font-size: ${convertToVwMob(20)};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const EmailButtonMob = styled.button`
  width: 100%;
  height: ${convertToVwMob(48)};
  background: ${({ theme }) => theme.colors.primaryBlue[5] || '#f8fdff'};
  border: 1px solid ${({ theme }) => theme.colors.primaryBlue[40] || '#2bc2e1'};
  border-radius: ${convertToVwMob(100)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  font-size: ${convertToVwMob(16)};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primaryBlue[40] || '#2bc2e1'};
  cursor: pointer;
  transition: background 0.15s;

  &:active {
    background: ${({ theme }) => theme.colors.primaryBlue[15] || '#def9ff'};
  }
`;

const EmailIcon = styled.div`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z' fill='%232BC2E1'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const EmailIconSmallBlue = styled.div`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z' fill='%232BC2E1'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const DownloadIcon = styled.div`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 16L7 11L8.4 9.55L11 12.15V4H13V12.15L15.6 9.55L17 11L12 16ZM6 20C5.45 20 4.97917 19.8042 4.5875 19.4125C4.19583 19.0208 4 18.55 4 18V15H6V18H18V15H20V18C20 18.55 19.8042 19.0208 19.4125 19.4125C19.0208 19.8042 18.55 20 18 20H6Z' fill='white'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const EmptyStateWrapper = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 20px;
  padding: ${convertToVwMob(24)} ${convertToVwMob(20)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(29)};
  width: 100%;
  box-sizing: border-box;
`;

const EmptyTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  gap: 0;
`;

const EmptyTitleBlue = styled.p`
  font-size: ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.primaryBlue[40]};
  margin: 0;
  line-height: 1.5;
`;

const EmptyTitleBlack = styled.p`
  font-size: ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
  line-height: 1.5;
`;

const ShareBtn = styled.button`
  background: #026478;
  border: none;
  border-radius: 50px;
  width: 100%;
  padding: ${convertToVwMob(14)} ${convertToVwMob(24)};
  min-width: ${convertToVwMob(240)};
  height: ${convertToVwMob(58)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  
  font-size: ${convertToVwMob(20)};
  font-weight: 700;
  color: white;
  white-space: nowrap;

  &:focus,
  &:active {
    border-radius: 50px;
    background: var(--PrimaryBlue-90, #416777);
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  }
`;

const ShareIcon = styled.img`
  width: ${convertToVwMob(30)};
  height: ${convertToVwMob(30)};
  object-fit: contain;
  aspect-ratio: 1/1;
`;

const ShareBottomSheetWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${convertToVwMob(20)};
`;

const ShareHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${convertToVwMob(24)};
  margin-bottom: ${convertToVwMob(24)};
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${convertToVwMob(12)};
`;

const ShareTitle = styled.h2`
  color: ${({ theme }) => theme.colors.grayScale[90]};
  ${({ theme }) => theme.typography.head.semiBold24}
  margin: 0;
  text-align: left;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.grayScale[60]};
  font-size: ${convertToVwMob(36)};
  font-weight: 300;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${convertToVwMob(44)};
  height: ${convertToVwMob(44)};
  line-height: 1;
  cursor: pointer;
`;

const ShareDescription = styled.p`
  color: ${({ theme }) => theme.colors.grayScale[70]};
  ${({ theme }) => theme.typography.contents.regular20}
  margin: 0;
  padding: 0;
  text-align: left;
`;

const ShareOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(16)};
  margin-bottom: ${convertToVwMob(20)};
  padding: 0 ${convertToVwMob(24)};
`;

const ShareOption = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${convertToVwMob(20)};
  background: ${({ theme }) => theme.colors.grayScale[0]};
  width: 100%;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 0;
`;

const KakaoIconBox = styled.div`
  width: ${convertToVwMob(60)};
  height: ${convertToVwMob(60)};
  background: #f9e000;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const KakaoIconImg = styled.img`
  width: ${convertToVwMob(34)};
  height: ${convertToVwMob(34)};
`;

const EmailIconBox = styled.div`
  width: ${convertToVwMob(60)};
  height: ${convertToVwMob(60)};
  background: #EBFCFF;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmailIconLarge = styled.img`
  width: ${convertToVwMob(34)};
  height: ${convertToVwMob(34)};
  object-fit: contain;
`;

const ShareOptionText = styled.span`
  ${({ theme }) => theme.typography.contents.medium20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  text-align: left;
  flex: 1;
`;

const CopyLinkButtonContainer = styled.div`
  padding: ${convertToVwMob(20)} ${convertToVwMob(30)};
  background: #F8F8F8;
`;

const CopyLinkButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  width: 100%;
  padding: ${convertToVwMob(12)} ${convertToVwMob(16)};
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: 1px solid #929292;
  border-radius: ${convertToVwMob(10)};
  color: ${({ theme }) => theme.colors.grayScale[90]};
  ${({ theme }) => theme.typography.contents.medium20}
  cursor: pointer;
`;

const CopyIcon = styled.div`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16 4H4V16H16V4ZM16 2C17.1 2 18 2.9 18 4V16C18 17.1 17.1 18 16 18H4C2.9 18 2 17.1 2 16V4C2 2.9 2.9 2 4 2H16ZM20 8V20H8V22H20C21.1 22 22 21.1 22 20V8H20Z' fill='%2317171B'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const SharePreviewSheet = styled.div`
  width: 100%;
  border-top-left-radius: ${convertToVwMob(24)};
  border-top-right-radius: ${convertToVwMob(24)};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.grayScale[0]};
`;

const SharePreviewHeader = styled.div`
  background: ${({ theme }) => theme.colors.grayScale[0]};
  padding: ${convertToVwMob(18)} ${convertToVwMob(20)} ${convertToVwMob(14)};
`;

const SharePreviewTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${convertToVwMob(12)};
`;

const SharePreviewTitle = styled.h3`
  margin: 0;
  font-family: Pretendard;
  font-size: ${convertToVwMob(24)};
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const SharePreviewCloseButton = styled.button`
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.grayScale[50]};
  font-size: ${convertToVwMob(32)};
  line-height: 1;
  padding: 0;
  width: ${convertToVwMob(32)};
  height: ${convertToVwMob(32)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const SharePreviewDesc = styled.p`
  margin: ${convertToVwMob(10)} 0 0;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  color: ${({ theme }) => theme.colors.grayScale[70]};
`;

const SharePreviewBody = styled.div`
  padding: ${convertToVwMob(20)} ${convertToVwMob(20)} ${convertToVwMob(18)};
  display: flex;
  justify-content: center;
  background: ${({ theme }) => theme.colors.grayScale[0]};
`;

const SharePreviewImage = styled.img`
  width: 100%;
  max-width: ${convertToVwMob(240)};
  object-fit: contain;
`;

const SharePreviewFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  padding: ${convertToVwMob(14)} ${convertToVwMob(20)} ${convertToVwMob(20)};
  display: flex;
  gap: ${convertToVwMob(8)};
  justify-content: flex-end;
  background: ${({ theme }) => theme.colors.grayScale[0]};
`;

const SharePreviewBackButton = styled.button`
  height: ${convertToVwMob(44)};
  min-width: ${convertToVwMob(92)};
  padding: 0 ${convertToVwMob(18)};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[30]};
  border-radius: ${convertToVwMob(6)};
  background: ${({ theme }) => theme.colors.grayScale[0]};
  color: ${({ theme }) => theme.colors.grayScale[70]};
  ${({ theme }) => theme.typography.contents.regular16}
  cursor: pointer;
`;

const SharePreviewSendButton = styled.button`
  height: ${convertToVwMob(44)};
  min-width: ${convertToVwMob(92)};
  padding: 0 ${convertToVwMob(20)};
  border: none;
  border-radius: ${convertToVwMob(6)};
  background: #0a667f;
  color: #fff;
  ${({ theme }) => theme.typography.contents.regular16}
  cursor: pointer;
`;

export default DonorManagementMob;
